import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Form, Input, Button, Card, message, Spin, Descriptions, Tag } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import apiClient from '../../services/apiClient';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'staff';
  department?: string;
  position?: string;
  permissions: string[];
  status: 'active' | 'inactive' | 'terminated';
  last_login?: string;
  created_at: string;
}

const SettingsPage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/me');
      if (response.data.success) {
        setProfile(response.data.data);
        profileForm.setFieldsValue({
          name: response.data.data.name,
          email: response.data.data.email,
        });
      }
    } catch (error) {
      message.error('Failed to load user profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [profileForm]);

  useEffect(() => {
    void fetchUserProfile();
  }, [fetchUserProfile]);

  const handleUpdateProfile = async (values: any) => {
    try {
      setSaveLoading(true);
      const response = await apiClient.put('/auth/update-profile', {
        name: values.name,
        email: values.email,
      });

      if (response.data.success) {
        setProfile(response.data.data);
        message.success('Profile updated successfully');
      } else {
        message.error(response.data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update profile');
      console.error(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdatePassword = async (values: any) => {
    try {
      setSaveLoading(true);
      const response = await apiClient.put('/auth/update-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      if (response.data.success) {
        message.success('Password updated successfully. Please log in again.');
        passwordForm.resetFields();
        // Optional: redirect to login or refresh token
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }, 2000);
      } else {
        message.error(response.data.message || 'Failed to update password');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update password');
      console.error(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const rolesConfig: Record<string, { color: string; label: string }> = {
    admin: { color: 'red', label: 'Administrator' },
    manager: { color: 'blue', label: 'Manager' },
    reception: { color: 'cyan', label: 'Reception' },
    cashier: { color: 'green', label: 'Cashier' },
    kitchen: { color: 'orange', label: 'Kitchen' },
    housekeeping: { color: 'purple', label: 'Housekeeping' },
    staff: { color: 'default', label: 'Staff' },
  };

  return (
    <div style={{ padding: '20px' }}>
      <Spin spinning={loading}>
        <Tabs
          items={[
            {
              key: '1',
              label: 'Profile',
              icon: <UserOutlined />,
              children: profile ? (
                <>
                  <Card style={{ marginBottom: '20px' }} title="User Information">
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="Full Name">{profile.name}</Descriptions.Item>
                      <Descriptions.Item label="Email">{profile.email}</Descriptions.Item>
                      <Descriptions.Item label="Role">
                        <Tag color={rolesConfig[profile.role?.toLowerCase() || 'staff'].color}>
                          {rolesConfig[profile.role?.toLowerCase() || 'staff'].label}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={profile.status === 'active' ? 'green' : 'red'}>
                          {profile.status.toUpperCase()}
                        </Tag>
                      </Descriptions.Item>
                      {profile.department && (
                        <Descriptions.Item label="Department">{profile.department}</Descriptions.Item>
                      )}
                      {profile.position && (
                        <Descriptions.Item label="Position">{profile.position}</Descriptions.Item>
                      )}
                      <Descriptions.Item label="Joined">
                        {dayjs(profile.created_at).format('MMM D, YYYY')}
                      </Descriptions.Item>
                      {profile.last_login && (
                        <Descriptions.Item label="Last Login">
                          {dayjs(profile.last_login).format('MMM D, YYYY h:mm A')}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>

                  <Card title="Edit Profile">
                    <Form
                      form={profileForm}
                      layout="vertical"
                      onFinish={handleUpdateProfile}
                      initialValues={{
                        name: profile.name,
                        email: profile.email,
                      }}
                    >
                      <Form.Item
                        label="Full Name"
                        name="name"
                        rules={[
                          { required: true, message: 'Please enter your full name' },
                          { min: 2, message: 'Name must be at least 2 characters' },
                        ]}
                      >
                        <Input placeholder="John Doe" />
                      </Form.Item>

                      <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                          { required: true, message: 'Please enter your email' },
                          { type: 'email', message: 'Please enter a valid email' },
                        ]}
                      >
                        <Input placeholder="john@example.com" />
                      </Form.Item>

                      <Button type="primary" htmlType="submit" loading={saveLoading}>
                        Update Profile
                      </Button>
                    </Form>
                  </Card>
                </>
              ) : (
                <p>No profile data available</p>
              ),
            },
            {
              key: '2',
              label: 'Security',
              icon: <LockOutlined />,
              children: (
                <Card title="Change Password" style={{ maxWidth: '500px' }}>
                  <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handleUpdatePassword}
                  >
                    <Form.Item
                      label="Current Password"
                      name="currentPassword"
                      rules={[
                        { required: true, message: 'Please enter your current password' },
                      ]}
                    >
                      <Input.Password placeholder="Enter current password" />
                    </Form.Item>

                    <Form.Item
                      label="New Password"
                      name="newPassword"
                      rules={[
                        { required: true, message: 'Please enter a new password' },
                        { min: 8, message: 'Password must be at least 8 characters' },
                        {
                          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message: 'Password must contain uppercase, lowercase, and numbers',
                        },
                      ]}
                    >
                      <Input.Password placeholder="Enter new password" />
                    </Form.Item>

                    <Form.Item
                      label="Confirm New Password"
                      name="confirmPassword"
                      dependencies={['newPassword']}
                      rules={[
                        { required: true, message: 'Please confirm your new password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="Confirm new password" />
                    </Form.Item>

                    <Button type="primary" danger htmlType="submit" loading={saveLoading}>
                      Change Password
                    </Button>
                  </Form>

                  <Card style={{ marginTop: '20px', background: '#fafafa' }} title="Password Requirements">
                    <ul>
                      <li>Minimum 8 characters</li>
                      <li>Must contain uppercase letters (A-Z)</li>
                      <li>Must contain lowercase letters (a-z)</li>
                      <li>Must contain numbers (0-9)</li>
                    </ul>
                  </Card>
                </Card>
              ),
            },
          ]}
        />
      </Spin>
    </div>
  );
};

export default SettingsPage;
